import type { Track } from '../../api.js';
import ModuleChip from '../../components/ModuleChip.js';
import RepoBadge from '../../components/RepoBadge.js';
import PipelineStepper from './PipelineStepper.js';
import { attentionStateFor, reviewFlags, stageFor, statusForTrackState } from './statusMap.js';
import { useTimeInState } from './useTimeInState.js';

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function BuildRow({
  track,
  recentCompleted,
  onOpen,
}: {
  track: Track;
  recentCompleted: Track[];
  onOpen: (id: string) => void;
}) {
  const { status } = statusForTrackState(track.state);
  const { stage, stageState } = stageFor(track);
  const { label, slow } = useTimeInState(track, recentCompleted);
  const flags = reviewFlags(track);
  const attention = attentionStateFor(track);

  return (
    <div className="build-row" data-status={status}>
      <button
        type="button"
        className="build-row-summary"
        onClick={() => onOpen(track.id)}
      >
        <span
          className={`build-dot${status === 'running' && !attention ? ' build-dot-pulse' : ''}`}
          data-status={status}
        />
        <div className="build-row-main">
          <div className="build-row-title">{track.title}</div>
          <div className="build-row-meta">
            <RepoBadge targetRepo={track.spec.target_repo} />
            <ModuleChip module={track.module} />
            {attention?.kind === 'halted' && (
              <span className="review-flag-chip" data-verdict="block" title={attention.reason ?? undefined}>
                ⛔ Halted{attention.since ? ` · ${relativeTime(attention.since)}` : ''}
              </span>
            )}
            {attention?.kind === 'needs_input' && (
              <span className="review-flag-chip" data-verdict="flag" title={attention.reason ?? undefined}>
                ⏸ Needs input{attention.since ? ` · ${relativeTime(attention.since)}` : ''}
              </span>
            )}
            {flags.map((f) => (
              <span key={f.key} className="review-flag-chip" data-verdict={f.verdict}>
                {f.label}
              </span>
            ))}
            <span>{relativeTime(track.updated_at)}</span>
          </div>
          <PipelineStepper
            stage={stage}
            stageState={stageState}
            timeLabel={label}
            slow={slow}
            className="build-row-stepper"
          />
        </div>
      </button>
    </div>
  );
}
